import * as Yup from "yup";

import { labelValues, softwareApplicationValues } from "./tagsValidationSchema";
import { SurveyAnswerType, SurveyQuestionType } from "../../features/Library/Surveys/types/models";
import { requiredTextFieldMinMax } from "./commonValidationSchemas";

const titleLengths = {
  min: 3,
  max: 64,
};

const descriptionLength = {
  max: 256,
  min: 3,
};
const endNoteTextMaxLength = 256;
const rateLabelMaxLength = 64;

const endNoteText = (): Yup.StringSchema<string | undefined, object> =>
  Yup.string().trim().max(endNoteTextMaxLength, `End Note Text cannot exceed ${endNoteTextMaxLength} characters`);

export const surveyInfo = Yup.object().shape({
  title: requiredTextFieldMinMax("Title", titleLengths.min, titleLengths.max),
  description: requiredTextFieldMinMax("Description", descriptionLength.min, descriptionLength.max),
  endNoteText: Yup.string().when("endNoteTextEnabled", {
    is: true,
    then: (_) => endNoteText(),
  }),
});

export const surveySettings = Yup.object().shape({
  labels: labelValues,
  softwareApplications: softwareApplicationValues,
});

const questionSchemas = (() => {
  const textQuestionValidation = Yup.string().trim().required("Question text is required");

  const ratingScaleValidator = Yup.object().shape({
    text: textQuestionValidation,
    minRateLabel: Yup.string().max(rateLabelMaxLength, `Rate Label cannot exceed ${rateLabelMaxLength} characters`),
    maxRateLabel: Yup.string().max(rateLabelMaxLength, `Rate Label cannot exceed ${rateLabelMaxLength} characters`),
  });

  return {
    [SurveyQuestionType.MultipleChoiceTextQuestion]: textQuestionValidation,
    [SurveyQuestionType.SingleChoiceTextQuestion]: textQuestionValidation,
    [SurveyQuestionType.FreeText]: textQuestionValidation,
    [SurveyQuestionType.RatingScale]: ratingScaleValidator,
  };
})();

const getQuestionValidationSchema = (type: SurveyQuestionType) => questionSchemas[type];

const answerSchemas = (() => {
  const textAnswerValidation = Yup.string().trim().required("Answer text is required");

  return {
    [SurveyAnswerType.Text]: textAnswerValidation,
  };
})();

const getAnswerValidationSchema = (type: SurveyAnswerType) => answerSchemas[type];

export const surveysContent = Yup.object().shape({
  questions: Yup.array().of(
    Yup.lazy((question: { questionType: SurveyQuestionType }) => {
      return Yup.object().shape({
        question: getQuestionValidationSchema(question.questionType),
        answers: Yup.array().of(
          Yup.lazy((answer: { answerType: SurveyAnswerType }) => {
            return Yup.object().shape({
              answer: getAnswerValidationSchema(answer.answerType),
            });
          }),
        ),
      });
    }),
  ),
});
